/**
 * THE CBD HUT — full search page coordination
 * - Normalises older search URLs into the product-led route
 * - Shares recent searches with the premium header search
 * - Restores focus to the results heading after a page-search submission
 * - Keeps the optional hero count accurate after AJAX filtering
 */
(() => {
  'use strict';

  const RECENT_STORAGE_KEY = '__cbh_header_recent_searches_v3__';
  const RECENT_LIMIT = 5;
  const RECENT_MINIMUM_LENGTH = 2;
  const FOCUS_STATUS_KEY = '__cbh_search_focus_status_v1__';

  const cleanTerm = (value) => String(value || '').trim().replace(/\s+/g, ' ');
  const normaliseTerm = (value) => cleanTerm(value).toLowerCase();
  const isSensitiveTerm = (value) => {
    const normalised = ` ${normaliseTerm(value).replace(/[^a-z0-9]+/g, ' ')} `;
    const terms = [
      'anxiety', 'anxious', 'sleep', 'sleeping', 'sleepless', 'insomnia',
      'pain', 'painful', 'arthritis', 'inflammation', 'inflammatory',
      'depression', 'depressed', 'stress', 'stressed', 'detox', 'cleanse',
      'cleansing', 'immune', 'immunity', 'focus support', 'memory support',
      'brain boost', 'brain boosting', 'cancer', 'tumour', 'tumor', 'epilepsy',
      'epileptic', 'seizure', 'seizures', 'diabetes', 'diabetic', 'migraine',
      'migraines', 'headache', 'headaches', 'nausea', 'nauseous', 'hypertension',
      'blood pressure', 'ptsd', 'post traumatic stress', 'adhd', 'attention deficit',
      'fibromyalgia', 'eczema', 'psoriasis', 'acne', 'relief', 'treat', 'treatment',
      'cure', 'curing', 'heal', 'healing', 'remedy', 'therapy', 'therapeutic', 'recovery',
    ];
    return terms.some((term) => normalised.includes(` ${term} `));
  };

  const normaliseSearchRoute = () => {
    const url = new URL(window.location.href);
    const legacyDisplayQuery = cleanTerm(url.searchParams.get('q_display'));
    let query = cleanTerm(url.searchParams.get('q'));
    let shouldReplace = false;

    if (legacyDisplayQuery) {
      query = legacyDisplayQuery;
      url.searchParams.set('q', legacyDisplayQuery);
      url.searchParams.delete('q_display');
      shouldReplace = true;
    }

    if (!query) return false;

    if (url.searchParams.get('type') !== 'product') {
      url.searchParams.set('type', 'product');
      shouldReplace = true;
    }

    if (!url.searchParams.get('options[prefix]')) {
      url.searchParams.set('options[prefix]', 'last');
      shouldReplace = true;
    }

    if (shouldReplace) {
      window.location.replace(url.toString());
      return true;
    }

    return false;
  };

  const getRecentSearches = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RECENT_STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];

      const seen = new Set();
      return stored
        .map(cleanTerm)
        .filter((term) => {
          const key = normaliseTerm(term);
          if (term.length < RECENT_MINIMUM_LENGTH || seen.has(key) || isSensitiveTerm(term)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, RECENT_LIMIT);
    } catch (error) {
      return [];
    }
  };

  const rememberSearch = (value) => {
    const term = cleanTerm(value);
    if (term.length < RECENT_MINIMUM_LENGTH || isSensitiveTerm(term)) return;

    try {
      const key = normaliseTerm(term);
      const recent = getRecentSearches().filter((item) => normaliseTerm(item) !== key);
      recent.unshift(term);
      window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(recent.slice(0, RECENT_LIMIT)));
    } catch (error) {
      // Search remains fully functional when browser storage is unavailable.
    }
  };

  const getSearchAction = () => {
    const formAction = document.querySelector('[data-cbh-search-form]')?.getAttribute('action') || '/search';
    return new URL(formAction, window.location.origin);
  };

  const productSearchUrl = (term) => {
    const url = getSearchAction();
    url.searchParams.set('q', cleanTerm(term));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return `${url.pathname}${url.search}`;
  };

  const setFocusMarker = () => {
    try {
      window.sessionStorage.setItem(FOCUS_STATUS_KEY, 'true');
    } catch (error) {
      // Focus restoration is an enhancement only.
    }
  };

  const restoreStatusFocus = (root) => {
    if (root?.dataset.cbhSearchPerformed !== 'true') return;

    let shouldFocus = false;
    try {
      shouldFocus = window.sessionStorage.getItem(FOCUS_STATUS_KEY) === 'true';
      window.sessionStorage.removeItem(FOCUS_STATUS_KEY);
    } catch (error) {
      shouldFocus = false;
    }

    if (!shouldFocus) return;

    const heading = root.querySelector('[data-cbh-search-page-heading]');
    if (!heading) return;

    window.requestAnimationFrame(() => heading.focus({ preventScroll: false }));
  };

  const renderRecentSearches = (root) => {
    const region = root.querySelector('[data-cbh-recent-searches]');
    const list = root.querySelector('[data-cbh-recent-list]');
    const clearButton = root.querySelector('[data-cbh-recent-clear]');
    const status = root.querySelector('[data-cbh-recent-status]');
    if (!region || !list) return;

    const render = () => {
      const recent = getRecentSearches();
      list.replaceChildren();

      recent.forEach((term) => {
        const link = document.createElement('a');
        link.className = 'cbh-search-hero__recent-link';
        link.href = productSearchUrl(term);
        link.textContent = term;
        link.addEventListener('click', setFocusMarker);
        list.append(link);
      });

      region.hidden = recent.length === 0;
    };

    clearButton?.addEventListener('click', () => {
      try {
        window.localStorage.removeItem(RECENT_STORAGE_KEY);
      } catch (error) {
        // The region still hides when storage is unavailable.
      }
      render();
      if (status) status.textContent = 'Recent searches cleared.';
    });

    render();
  };

  const bindSearchForms = (root) => {
    root.querySelectorAll('[data-cbh-search-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        const input = form.querySelector('input[name="q"]');
        const term = cleanTerm(input?.value || '');

        if (!term) {
          event.preventDefault();
          input?.focus({ preventScroll: false });
          return;
        }

        rememberSearch(term);
        setFocusMarker();
      });
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      try {
        const url = new URL(link.href, window.location.origin);
        const searchPathname = getSearchAction().pathname;
        if (url.origin === window.location.origin && url.pathname === searchPathname && cleanTerm(url.searchParams.get('q'))) {
          setFocusMarker();
        }
      } catch (error) {
        // Ignore malformed or non-navigation links.
      }
    });
  };

  const extractCount = (value) => {
    const match = String(value || '')
      .replace(/\u00a0/g, ' ')
      .match(/(\d[\d,]*)/);
    if (!match) return null;
    const count = Number.parseInt(match[1].replace(/,/g, ''), 10);
    return Number.isFinite(count) ? count : null;
  };

  const syncHeroCount = (countText) => {
    const countNode = document.querySelector('[data-cbh-search-hero-count]');
    if (!countNode) return;

    const count = extractCount(countText);
    if (count === null) return;

    if (count === 0) {
      countNode.textContent = 'No products found';
    } else {
      countNode.textContent = `${count.toLocaleString('en-GB')} ${count === 1 ? 'product' : 'products'}`;
    }
  };

  const applyHeadingTemplate = (template, term) => String(template || '').replace('[search_term]', term);

  const setHeroState = (root, state) => {
    if (!root || !state) return;

    ['idle', 'results', 'no-results', 'filtered-empty'].forEach((candidate) => {
      root.classList.toggle(`cbh-search-hero--${candidate}`, candidate === state);
    });
    root.dataset.cbhSearchState = state;
  };

  const syncHeroState = (countText) => {
    const root = document.querySelector('[data-cbh-search-page]');
    if (!root || root.dataset.cbhSearchPerformed !== 'true') return;
    if (root.dataset.cbhSearchSensitive === 'true') return;

    const count = extractCount(countText);
    const heading = root.querySelector('[data-cbh-search-page-heading]');
    const eyebrow = root.querySelector('[data-cbh-search-page-eyebrow]');
    const filteredEmpty = Boolean(document.querySelector('[data-cbh-search-filter-empty]'));

    if (filteredEmpty) {
      setHeroState(root, 'filtered-empty');
      if (heading && root.dataset.cbhFilteredEmptyHeading) {
        heading.textContent = root.dataset.cbhFilteredEmptyHeading;
      }
      if (eyebrow && root.dataset.cbhFilteredEmptyEyebrow) {
        eyebrow.textContent = root.dataset.cbhFilteredEmptyEyebrow;
      }
    } else if (count !== null && count > 0) {
      setHeroState(root, 'results');
      if (heading && root.dataset.cbhResultsHeadingTemplate) {
        heading.textContent = applyHeadingTemplate(
          root.dataset.cbhResultsHeadingTemplate,
          root.dataset.cbhSearchTerm || ''
        );
      }
      if (eyebrow && root.dataset.cbhResultsEyebrow) {
        eyebrow.textContent = root.dataset.cbhResultsEyebrow;
      }
    }

    syncHeroCount(countText);
  };

  if (normaliseSearchRoute()) return;

  const root = document.querySelector('[data-cbh-search-page]');
  if (root) {
    if (root.dataset.cbhSearchPerformed === 'true') {
      rememberSearch(root.dataset.cbhSearchTerm || '');
    }

    bindSearchForms(root);
    renderRecentSearches(root);
    restoreStatusFocus(root);
  }

  document.addEventListener('cbh:collection:updated', (event) => {
    syncHeroState(event.detail?.countText || '');
  });
})();
